import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Monitor, LogOut } from 'lucide-react';
import { PageHeader } from '../../../components/layout/PageHeader';
import { DataView } from '../../../components/ui/DataView';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../components/ui/Toast';
import { usePageTitle } from '../../../lib/hooks';
import { useMySessions, useRevokeMySession, type MySession } from '../../../features/settings/network/sessions.api';

export const Route = createFileRoute('/_auth/settings/sessions')({
  component: ActiveSessionsPage,
});

function ActiveSessionsPage() {
  usePageTitle('Active Sessions');
  const { toast } = useToast();
  const { data, isLoading, isError, error, refetch } = useMySessions();
  const revokeMutation = useRevokeMySession();
  const [sessionToRevoke, setSessionToRevoke] = useState<MySession | null>(null);

  const handleRevoke = async () => {
    if (!sessionToRevoke) return;
    try {
      await revokeMutation.mutateAsync(sessionToRevoke.id);
      toast({
        title: 'Session revoked',
        description: sessionToRevoke.isCurrent
          ? 'You have been signed out of this device.'
          : 'The device has been signed out.',
        type: 'success',
      });
      setSessionToRevoke(null);
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to revoke session';
      toast({ title: 'Error', description: message, type: 'error' });
    }
  };

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <PageHeader
        title="Active Sessions"
        subtitle="Devices and browsers currently signed in to your account."
      />

      <DataView
        query={{ isLoading, isError, error, data, refetch }}
        emptyTitle="No active sessions"
        emptyDescription="You don't have any active sessions right now."
      >
        {(sessions) => (
          <div className="flex flex-col gap-3 max-w-2xl">
            {sessions.map((session) => (
              <Card key={session.id} className="p-4 flex items-center justify-between gap-4 border border-border">
                <div className="flex items-start gap-3 min-w-0">
                  <Monitor className="h-5 w-5 text-text-secondary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary truncate">
                        {session.userAgent || 'Unknown device'}
                      </span>
                      {session.isCurrent && (
                        <span className="text-xs font-semibold uppercase tracking-wide text-accent-700 bg-accent-50 border border-accent-200 rounded-full px-2 py-0.5">
                          This device
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-secondary font-mono mt-0.5">
                      {session.ipAddress || 'Unknown IP'}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5">
                      Last active {new Date(session.updatedAt).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSessionToRevoke(session)}
                  className="text-danger-500 hover:text-danger-600 shrink-0"
                >
                  <LogOut className="h-4 w-4 mr-1.5" />
                  {session.isCurrent ? 'Sign out' : 'Revoke'}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </DataView>

      <ConfirmDialog
        open={!!sessionToRevoke}
        title={sessionToRevoke?.isCurrent ? 'Sign out of this device?' : 'Revoke session?'}
        description={
          sessionToRevoke?.isCurrent
            ? 'You will be signed out of your current session and redirected to login.'
            : `Are you sure you want to sign out "${sessionToRevoke?.userAgent || 'this device'}"? It will need to sign in again.`
        }
        confirmLabel={revokeMutation.isPending ? 'Revoking...' : 'Revoke'}
        onConfirm={handleRevoke}
        onCancel={() => setSessionToRevoke(null)}
        loading={revokeMutation.isPending}
      />
    </div>
  );
}
